import { Component, ElementRef, inject, OnInit, ViewChild } from "@angular/core";
import {
  faArrowLeft,
  faEdit,
  faKey,
  faMailBulk,
  faPhone,
  faSms,
  faTrash,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import { CustomerService, StoreService, UserService } from "../../../_services";
import { APTechnicianHash, Technician, TechnicianUpdateHash } from "../../../_models/technician.interface";
import { Store } from "../../../_models/store.interface";
import { INTERNAL_SHIPPING_PROJECT_ID } from "../../../_models/internal-project.const";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { Validators, ReactiveFormsModule, FormsModule, FormBuilder } from "@angular/forms";
import { PADataControl } from "../../planning-assistant/singletons/pa-data-control";
import { Customer } from "../../../_models";
import { faSignIn } from "@fortawesome/pro-solid-svg-icons";
import { RoleService } from "../../../_services/role.service";
import { Role } from "../../../_models/role.interface";

import { CommonModule } from "@angular/common";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";

import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import * as mapboxgl from "mapbox-gl";
import { PAUtil } from "../../planning-assistant/classes/util";
import { MapBoxService } from "../../../_services/mapbox.service";
import { GeocoderEvent } from "../../../_models/mapbox.interface";
import { RecordNavigationComponent } from "../../_shared/record-navigation/record-navigation.component";

@Component({
  selector: "hawk-user-edit",
  templateUrl: "./user-edit.component.html",
  styleUrls: ["./user-edit.component.scss"],
  standalone: true,
  imports: [RecordNavigationComponent, ReactiveFormsModule, FormsModule, CommonModule, FontAwesomeModule, RouterLink],
  host: {
    class: "app-page",
  },
})
export class UserEditComponent implements OnInit {
  protected readonly faUser = faUser;
  private fb = inject(FormBuilder);
  user?: APTechnicianHash;
  customers: Customer[] = [];
  // Filialen des internen Materialversand-Projekts für die Versandfiliale-Auswahl
  protected readonly internalShippingProjectId = INTERNAL_SHIPPING_PROJECT_ID;
  internalShippingStores: Store[] = [];
  roles: Role[] = [];
  checkedRoles: { role: Role; checked: boolean }[] = [];
  dispatcherColor: string | null = null;
  subcontractors: Technician[] = [];
  addressGeocoder?: MapboxGeocoder;
  companyAddressGeocoder?: MapboxGeocoder;

  @ViewChild("checklistWarningDialog") checklistWarningDialog: ElementRef<HTMLDialogElement>;

  appConfig: Record<string, boolean> = {
    show_debug: false,
    request_log_upload: false,
    disable_checklist_validation: false,
  };

  protected userForm = this.fb.group({
    first_name: this.fb.control<string>("", Validators.required),
    last_name: this.fb.control<string>("", Validators.required),
    company: this.fb.control<string>(""),
    repairer: this.fb.control<string>(""),
    email1: this.fb.control<string>("", [Validators.email, Validators.required]),
    email2: this.fb.control<string>(""),
    email_moreapp: this.fb.control<string>("", Validators.email),
    email_partner1: this.fb.control<string>("", Validators.email),
    email_partner2: this.fb.control<string>(""),
    customer_id: this.fb.control<number>(null),
    employer_id: this.fb.control<number>(null),
    internal_shipping_store_id: this.fb.control<number>(null),
    phone1: this.fb.control<string>("", Validators.pattern("[- +()0-9]+")),
    phone2: this.fb.control<string>(""),
    telephone_dispatching: this.fb.control<string>("", Validators.pattern("[- +()0-9]+")),
    more_contact_info: this.fb.control<string>(""),
    street: this.fb.control<string>({ value: "", disabled: true }),
    zip: this.fb.control<string>({ value: "", disabled: true }, [
      Validators.pattern("^([a-zA-Z]{2}-)?[0-9]{2,5}( [a-zA-Z]{0,2})?$"),
    ]),
    city: this.fb.control<string>({ value: "", disabled: true }),
    country: this.fb.control<string>({ value: "", disabled: true }),
    state: this.fb.control<string>({ value: "", disabled: true }),
    lat: this.fb.control<number>(null),
    lng: this.fb.control<number>(null),
    company_address_company: this.fb.control<string>(""),
    company_address_country: this.fb.control<string>({ value: "", disabled: true }),
    company_address_state: this.fb.control<string>({ value: "", disabled: true }),
    company_address_firstname: this.fb.control<string>(""),
    company_address_lastname: this.fb.control<string>(""),
    company_address_street: this.fb.control<string>({ value: "", disabled: true }),
    company_address_zip: this.fb.control<string>({ value: "", disabled: true }, [
      Validators.pattern("^([a-zA-Z]{2}-)?[0-9]{2,5}( [a-zA-Z]{0,2})?$"),
    ]),
    company_address_city: this.fb.control<string>({ value: "", disabled: true }),
    card_nr_packstation: this.fb.control<string>(""),
    pickpoint_nr: this.fb.control<string>(""),
    xml_id: this.fb.control<string>(""),
    comment: this.fb.control<string>(""),
  });

  constructor(
    public userService: UserService,
    public customerService: CustomerService,
    public storeService: StoreService,
    public roleService: RoleService,
    public route: ActivatedRoute,
    public mapBoxService: MapBoxService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    const id = Number.parseInt(this.route.snapshot.paramMap.get("id"));
    this.loadUser(id);
    this.loadCustomers();
    this.loadInternalShippingStores();
    this.loadRoles();
    this.loadSubcontractors();
    this.setGeocoder();
  }

  public async setGeocoder(): Promise<void> {
    this.addressGeocoder = new MapboxGeocoder({
      accessToken: this.mapBoxService.accessToken,
      marker: false,
      language: "de-DE",
      mapboxgl: mapboxgl,
      placeholder: "Adresse suchen",
    });
    this.companyAddressGeocoder = new MapboxGeocoder({
      accessToken: this.mapBoxService.accessToken,
      marker: false,
      language: "de-DE",
      mapboxgl: mapboxgl,
      placeholder: "Firmenadresse suchen",
    });
    while (!document.getElementById("address_geocoder") || !document.getElementById("company_address_geocoder")) {
      await PAUtil.sleep(100);
    }
    const address_geocoder_elem = document.getElementById("address_geocoder");
    const company_address_geocoder_elem = document.getElementById("company_address_geocoder");
    this.addressGeocoder.addTo(address_geocoder_elem);
    this.companyAddressGeocoder.addTo(company_address_geocoder_elem);

    this.addressGeocoder.on("result", (event) => {
      this.onAddressGeocoderEvent(event);
    });
    this.companyAddressGeocoder.on("result", (event) => {
      this.onCompanyAddressGeocoderEvent(event);
    });
  }

  loadUser(id: number): void {
    this.userService.getAPUser(id).subscribe(
      (user) => {
        this.user = user;
        this.resetForm();
      },
      (err) => {
        console.log(err);
      },
    );
  }

  loadCustomers(): void {
    this.customerService.loadCustomers().subscribe(
      (customers) => {
        this.customers = customers.sort((c1, c2) => (c1.name.toLowerCase() < c2.name.toLowerCase() ? -1 : 1));
      },
      (err) => {
        console.log(err);
      },
    );
  }

  loadInternalShippingStores(): void {
    this.storeService.getProjectStores(this.internalShippingProjectId).subscribe(
      (stores) => {
        this.internalShippingStores = stores.sort((s1, s2) =>
          s1.name.toLowerCase() < s2.name.toLowerCase() ? -1 : 1,
        );
      },
      (err) => {
        console.log(err);
      },
    );
  }

  loadRoles(): void {
    this.roleService.getRoles().subscribe(
      (roles) => {
        this.roles = roles;
        this.resetRoles();
      },
      (err) => {
        console.log(err);
      },
    );
  }

  loadSubcontractors(): void {
    this.userService.getSubcontractors().subscribe(
      (subcontractors) => {
        this.subcontractors = subcontractors;
      },
      (err) => {
        console.log(err);
      },
    );
  }

  resetForm(): void {
    this.userForm.setValue({
      first_name: this.user.first_name,
      last_name: this.user.last_name,
      company: this.user.company,
      repairer: this.user.repairer,
      email1: this.user.email1,
      email2: this.user.email2,
      email_moreapp: this.user.email_moreapp,
      email_partner1: this.user.email_partner1,
      email_partner2: this.user.email_partner2,
      customer_id: this.user.customer?.id || null,
      employer_id: this.user.employer?.id || null,
      internal_shipping_store_id: this.user.internal_shipping_store?.id || null,
      phone1: this.user.phone1,
      phone2: this.user.phone2,
      telephone_dispatching: this.user.telephone_dispatching,
      more_contact_info: this.user.more_contact_info,
      street: this.user.street,
      zip: this.user.zip,
      city: this.user.city,
      country: this.user.country,
      state: this.user.state,
      lat: this.user.lat,
      lng: this.user.lng,
      company_address_company: this.user.company_address_company,
      company_address_country: this.user.company_address_country,
      company_address_state: this.user.company_address_state,
      company_address_firstname: this.user.company_address_firstname,
      company_address_lastname: this.user.company_address_lastname,
      company_address_street: this.user.company_address_street,
      company_address_zip: this.user.company_address_zip,
      company_address_city: this.user.company_address_city,
      card_nr_packstation: this.user.card_nr_packstation,
      pickpoint_nr: this.user.pickpoint_nr,
      xml_id: this.user.xml_id,
      comment: this.user.comment,
    });

    this.resetRoles();
    this.resetAppConfig();
  }

  resetAppConfig(): void {
    const config = this.user?.app_config || {};
    this.appConfig = {
      show_debug: !!config["show_debug"],
      request_log_upload: !!config["request_log_upload"],
      disable_checklist_validation: !!config["disable_checklist_validation"],
    };
  }

  // Warndialog für disable_checklist_validation
  onDisableChecklistValidationChange(checked: boolean): void {
    if (checked) {
      // Sofort zurücksetzen, Dialog entscheidet
      this.appConfig.disable_checklist_validation = false;
      this.checklistWarningDialog.nativeElement.showModal();
    }
  }

  confirmDisableChecklistValidation(): void {
    this.appConfig.disable_checklist_validation = true;
    this.checklistWarningDialog.nativeElement.close();
  }

  cancelDisableChecklistValidation(): void {
    this.checklistWarningDialog.nativeElement.close();
  }

  // Dispatcher- oder Supervisor-Dispatcher-Rolle (ID 4 oder 5) ausgewählt
  get hasDispatcherRole(): boolean {
    return this.checkedRoles.some((cr) => cr.checked && (cr.role.id === 4 || cr.role.id === 5));
  }

  resetRoles(): void {
    this.checkedRoles = this.roles
      .filter((r) => !r.name.includes("CRM"))
      .map((role) => {
        return { role: role, checked: this.user?.roles.map((r) => r.id).includes(role.id) };
      });
    this.dispatcherColor = this.user?.dispatcher_color || null;
  }

  // Speicherfehler (z.B. 422 wegen bereits vergebener Versandfiliale) — wird
  // über dem Speichern-Button angezeigt; navigiert wird nur bei Erfolg.
  saveError: string | null = null;

  async onSubmit() {
    this.saveError = null;
    // Bei Fehlern auf der Seite bleiben, damit der Konflikt (z.B. doppelt
    // vergebene Versandfiliale) behoben werden kann — vorher wurde trotz
    // 422 kommentarlos zur Show-Seite navigiert.
    if (!(await this.updateUser())) return;
    if (!(await this.updateRoles())) return;
    this.router.navigate(["/benutzer/" + this.user.id]);
  }

  updateUser(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.userService.updateUser(this.user.id, this.getUpdateHash()).subscribe(
        (user) => {
          this.user = user;
          resolve(true);
        },
        (error) => {
          console.log(error);
          this.saveError = this.extractErrorMessage(error);
          resolve(false);
        },
      );
    });
  }

  // Der globale ErrorInterceptor liefert Fehler bereits als lesbaren String
  // (inkl. eingesammelter Rails-Validierungsmeldungen). Objekt-Handling bleibt
  // als Absicherung, falls der Interceptor einmal Original-Fehler durchreicht.
  private extractErrorMessage(error: unknown): string {
    if (typeof error === "string" && error.trim().length > 0) return error;

    const data = (error as { error?: unknown })?.error;
    if (data && typeof data === "object") {
      const parts = Object.entries(data as Record<string, unknown>)
        .filter(([, msgs]) => Array.isArray(msgs))
        .map(([, msgs]) => (msgs as string[]).join(", "));
      if (parts.length > 0) return parts.join(" — ");
    }
    return "Speichern fehlgeschlagen — bitte Eingaben prüfen und erneut versuchen.";
  }

  updateRoles(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      const not_crm_roles = this.roles.filter((r) => !r.name.includes("CRM"));
      const add_roles_with_ids: number[] = not_crm_roles
        .filter((r) => this.roleIsChecked(r.id) && !this.userHasRole(r.id))
        .map((r) => r.id);
      const delete_roles_with_ids: number[] = not_crm_roles
        .filter((r) => !this.roleIsChecked(r.id) && this.userHasRole(r.id))
        .map((r) => r.id);

      this.userService.updateUserRoles(this.user.id, add_roles_with_ids, delete_roles_with_ids).subscribe(
        (user) => {
          this.user = user;
          resolve(true);
        },
        (error) => {
          console.log(error);
          this.saveError = this.extractErrorMessage(error);
          resolve(false);
        },
      );
    });
  }

  roleIsChecked(id: number): boolean {
    return !!this.checkedRoles.find((cr) => cr.role.id == id && cr.checked);
  }

  userHasRole(id: number): boolean {
    return !!this.user.roles.find((user_role) => user_role.id == id);
  }

  getUpdateHash(): TechnicianUpdateHash {
    const controls = this.userForm.controls;
    return {
      firstname: controls["first_name"].value,
      lastname: controls["last_name"].value,
      email: controls["email1"].value,
      email2: controls["email2"].value,
      email_moreapp: controls["email_moreapp"].value,
      email_partner: controls["email_partner1"].value,
      email_partner2: controls["email_partner2"].value,
      customer_id: controls["customer_id"].value,
      telephone1: controls["phone1"].value,
      telephone2: controls["phone2"].value,
      telephone_dispatching: controls["telephone_dispatching"].value,
      more_contact_info: controls["more_contact_info"].value,
      address_company: controls["company"].value,
      address_street: controls["street"].value,
      address_street_no: "",
      address_city: controls["city"].value,
      address_zip: controls["zip"].value,
      address_country: controls["country"].value,
      address_state: controls["state"].value,
      company_address_company: controls["company_address_company"].value,
      company_address_street: controls["company_address_street"].value,
      company_address_city: controls["company_address_city"].value,
      company_address_zip: controls["company_address_zip"].value,
      company_address_country: controls["company_address_country"].value,
      company_address_state: controls["company_address_state"].value,
      company_address_firstname: controls["company_address_firstname"].value,
      company_address_lastname: controls["company_address_lastname"].value,
      company_address_street_no: "",
      employer_id: controls["employer_id"].value,
      internal_shipping_store_id: controls["internal_shipping_store_id"].value || null,
      card_nr_packstation: controls["card_nr_packstation"].value,
      pickpoint_nr: controls["pickpoint_nr"].value,
      xml_id: controls["xml_id"].value,
      address_latitude: controls["lat"].value,
      address_longitude: controls["lng"].value,
      comment: controls["comment"].value,
      dispatcher_color: this.dispatcherColor || null,
      app_config: this.appConfig,
    };
  }

  private onAddressGeocoderEvent(event: GeocoderEvent) {
    const { country, region, city, post_code, street, street_no, center, name } = PAUtil.getGeocoderEventAddress(event);

    const address_country_control = this.userForm.controls["country"];
    address_country_control.setValue(country);
    address_country_control.markAsTouched();
    address_country_control.updateValueAndValidity();
    const address_state_control = this.userForm.controls["state"];
    address_state_control.setValue(region);
    address_state_control.markAsTouched();
    address_state_control.updateValueAndValidity();
    const address_city_control = this.userForm.controls["city"];
    address_city_control.setValue(city);
    address_city_control.markAsTouched();
    address_city_control.updateValueAndValidity();
    const address_zip_control = this.userForm.controls["zip"];
    address_zip_control.setValue(post_code);
    address_zip_control.markAsTouched();
    address_zip_control.updateValueAndValidity();
    const address_street_control = this.userForm.controls["street"];
    address_street_control.setValue(street + " " + street_no);
    address_street_control.markAsTouched();
    address_street_control.updateValueAndValidity();

    const latitude_control = this.userForm.controls["lat"];
    latitude_control.setValue(center[1]);
    latitude_control.markAsTouched();
    latitude_control.updateValueAndValidity();
    const longitude_control = this.userForm.controls["lng"];
    longitude_control.setValue(center[0]);
    longitude_control.markAsTouched();
    longitude_control.updateValueAndValidity();

    this.userForm.updateValueAndValidity();
  }

  private onCompanyAddressGeocoderEvent(event: any) {
    const { country, region, city, post_code, street, street_no, center, name } = PAUtil.getGeocoderEventAddress(event);

    const address_country_control = this.userForm.controls["company_address_country"];
    address_country_control.setValue(country);
    address_country_control.markAsTouched();
    address_country_control.updateValueAndValidity();
    const address_state_control = this.userForm.controls["company_address_state"];
    address_state_control.setValue(region);
    address_state_control.markAsTouched();
    address_state_control.updateValueAndValidity();
    const address_city_control = this.userForm.controls["company_address_city"];
    address_city_control.setValue(city);
    address_city_control.markAsTouched();
    address_city_control.updateValueAndValidity();
    const address_zip_control = this.userForm.controls["company_address_zip"];
    address_zip_control.setValue(post_code);
    address_zip_control.markAsTouched();
    address_zip_control.updateValueAndValidity();
    const address_street_control = this.userForm.controls["company_address_street"];
    address_street_control.setValue(street + " " + street_no);
    address_street_control.markAsTouched();
    address_street_control.updateValueAndValidity();

    this.userForm.updateValueAndValidity();
  }

  protected readonly faMailBulk = faMailBulk;
  protected readonly PADataControl = PADataControl;
  protected readonly faPhone = faPhone;
  protected readonly faSms = faSms;
  protected readonly faArrowLeft = faArrowLeft;
  protected readonly faKey = faKey;
  protected readonly faEdit = faEdit;
  protected readonly faTrash = faTrash;
  protected readonly faSignIn = faSignIn;
}
